import React, { useState } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { useParams, useHistory } from 'react-router-dom'
import {
  Box,
  Button,
  CardContent,
  CircularProgress,
  Typography,
  Alert,
  Snackbar,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  PlayArrow as PlayIcon,
  CalendarToday as CalendarIcon,
  Code as CodeIcon,
  Message as MessageIcon,
} from '@material-ui/icons'
import dayjs from 'dayjs'
import {
  GET_AI_COACHING_INTERVENTION,
  TRIGGER_INTERVENTION,
} from '../../../../Apollo/Queries/AiCoachingInterventions'
import {
  PageContainer,
  HeaderSection,
  BackButton,
  TitleSection,
  Title,
  Subtitle,
  StatusChip,
  InfoCard,
  InfoGrid,
  InfoItem,
  InfoLabel,
  InfoValue,
  SectionTitle,
  LoadingContainer,
  ErrorContainer,
  MessageBox,
  TriggerDataBox,
  ActionBar,
} from './AiCoachingDetail.styled'

interface RouteParams {
  id: string
}

interface AiCoachingInterventionDetail {
  id: number
  identifier: string
  version: string
  trigger: string
  shouldIntervene: boolean
  createdAt: string
  message?: string | null
  editedMessage?: string | null
  status?: string | null
  coachRating?: string | null
  coachFeedback?: string | null
  claimedAt?: string | null
  emailedAt?: string | null
  postedToFeedAt?: string | null
  declinedAt?: string | null
  declineReason?: string | null
  user?: {
    id: number
    firstName?: string | null
    lastName?: string | null
    email: string
  } | null
  claimedBy?: {
    id: number
    firstName?: string | null
    lastName?: string | null
    email: string
  } | null
}

export const AiCoachingDetail: React.FC = () => {
  const { id } = useParams<RouteParams>()
  const history = useHistory()
  const interventionId = parseInt(id, 10)

  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({ open: false, message: '', severity: 'success' })

  const { data, loading, error } = useQuery<{
    aiCoachingIntervention: AiCoachingInterventionDetail
  }>(GET_AI_COACHING_INTERVENTION, {
    variables: { id: interventionId },
    skip: !interventionId,
  })

  const [triggerIntervention] = useMutation(TRIGGER_INTERVENTION, {
    onCompleted: () => {
      setSnackbar({
        open: true,
        message: 'New intervention triggered successfully!',
        severity: 'success',
      })
      // Navigate to the list view to see the new intervention
      setTimeout(() => {
        history.push('/internal/self-serve/ai-coaching-admin')
      }, 2000)
    },
    onError: (err) => {
      setSnackbar({
        open: true,
        message: `Error: ${err.message}`,
        severity: 'error',
      })
    },
  })

  const intervention = data?.aiCoachingIntervention

  const handleBack = () => {
    history.push('/internal/self-serve/ai-coaching-admin')
  }

  const handleRetrigger = async () => {
    if (intervention?.user?.id && intervention?.identifier) {
      await triggerIntervention({
        variables: {
          identifier: intervention.identifier,
          userId: intervention.user.id,
        },
      })
    }
  }

  const navigateToUser = () => {
    if (intervention?.user?.id) {
      history.push(`/internal/self-serve/user-admin/${intervention.user.id}`)
    }
  }

  if (loading) {
    return (
      <LoadingContainer>
        <CircularProgress />
      </LoadingContainer>
    )
  }

  if (error || !intervention) {
    return (
      <ErrorContainer>
        <Alert severity='error'>
          {error ? error.message : 'Intervention not found'}
        </Alert>
        <Button
          variant='outlined'
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mt: 2 }}
        >
          Back to List
        </Button>
      </ErrorContainer>
    )
  }

  // Parse trigger data
  let triggerData: Record<string, unknown> = {}
  try {
    triggerData = JSON.parse(intervention.trigger) as Record<string, unknown>
  } catch (e) {
    triggerData = { error: 'Invalid trigger data' }
  }

  return (
    <PageContainer>
      <HeaderSection>
        <BackButton onClick={handleBack}>
          <ArrowBackIcon />
        </BackButton>
        <Box flex={1}>
          <TitleSection>
            <Title>Intervention #{intervention.id}</Title>
            <StatusChip
              status={intervention.shouldIntervene}
              icon={
                intervention.shouldIntervene ? (
                  <CheckCircleIcon />
                ) : (
                  <CancelIcon />
                )
              }
              label={intervention.shouldIntervene ? 'Intervened' : 'Skipped'}
            />
          </TitleSection>
          <Subtitle>
            {intervention.identifier} v{intervention.version}
          </Subtitle>
        </Box>
      </HeaderSection>

      <Box mb={3}>
        <InfoCard>
          <CardContent>
            <SectionTitle>
              <PersonIcon />
              User Information
            </SectionTitle>
            {intervention.user ? (
              <InfoGrid>
                <InfoItem>
                  <InfoLabel>Name</InfoLabel>
                  <InfoValue>
                    {intervention.user.firstName} {intervention.user.lastName}
                  </InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Email</InfoLabel>
                  <InfoValue>{intervention.user.email}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>User ID</InfoLabel>
                  <InfoValue>#{intervention.user.id}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <Button
                    variant='outlined'
                    size='small'
                    onClick={navigateToUser}
                  >
                    View User Details
                  </Button>
                </InfoItem>
              </InfoGrid>
            ) : (
              <Typography color='textSecondary'>
                No user information available
              </Typography>
            )}
          </CardContent>
        </InfoCard>
      </Box>

      <Box mb={3}>
        <InfoCard>
          <CardContent>
            <SectionTitle>
              <CalendarIcon />
              Intervention Details
            </SectionTitle>
            <InfoGrid>
              <InfoItem>
                <InfoLabel>Type</InfoLabel>
                <InfoValue>{intervention.identifier}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Version</InfoLabel>
                <InfoValue>{intervention.version}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Created At</InfoLabel>
                <InfoValue>
                  {dayjs(intervention.createdAt).format(
                    'MMM D, YYYY h:mm:ss A'
                  )}
                </InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Status</InfoLabel>
                <InfoValue>
                  {intervention.shouldIntervene
                    ? 'Intervention Sent'
                    : 'Skipped (Criteria Not Met)'}
                </InfoValue>
              </InfoItem>
              {intervention.status && (
                <InfoItem>
                  <InfoLabel>Processing Status</InfoLabel>
                  <InfoValue>
                    {intervention.status.replace(/_/g, ' ')}
                  </InfoValue>
                </InfoItem>
              )}
            </InfoGrid>
          </CardContent>
        </InfoCard>
      </Box>

      {(intervention.claimedBy ||
        intervention.coachRating ||
        intervention.coachFeedback) && (
        <Box mb={3}>
          <InfoCard>
            <CardContent>
              <SectionTitle>
                <PersonIcon />
                Coach Review
              </SectionTitle>
              <InfoGrid>
                {intervention.claimedBy && (
                  <>
                    <InfoItem>
                      <InfoLabel>Claimed By</InfoLabel>
                      <InfoValue>
                        {intervention.claimedBy.firstName}{' '}
                        {intervention.claimedBy.lastName}
                      </InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Claimed At</InfoLabel>
                      <InfoValue>
                        {intervention.claimedAt
                          ? dayjs(intervention.claimedAt).format(
                              'MMM D, YYYY h:mm A'
                            )
                          : '-'}
                      </InfoValue>
                    </InfoItem>
                  </>
                )}
                {intervention.coachRating && (
                  <InfoItem>
                    <InfoLabel>Rating</InfoLabel>
                    <InfoValue>
                      <Typography
                        component='span'
                        sx={{
                          color:
                            intervention.coachRating === 'GOOD'
                              ? '#4caf50'
                              : '#f44336',
                          fontWeight: 'medium',
                        }}
                      >
                        {intervention.coachRating}
                      </Typography>
                    </InfoValue>
                  </InfoItem>
                )}
                {intervention.coachFeedback && (
                  <InfoItem style={{ gridColumn: '1 / -1' }}>
                    <InfoLabel>Feedback</InfoLabel>
                    <InfoValue>{intervention.coachFeedback}</InfoValue>
                  </InfoItem>
                )}
                {intervention.emailedAt && (
                  <InfoItem>
                    <InfoLabel>Emailed At</InfoLabel>
                    <InfoValue>
                      {dayjs(intervention.emailedAt).format(
                        'MMM D, YYYY h:mm A'
                      )}
                    </InfoValue>
                  </InfoItem>
                )}
                {intervention.postedToFeedAt && (
                  <InfoItem>
                    <InfoLabel>Posted to Feed At</InfoLabel>
                    <InfoValue>
                      {dayjs(intervention.postedToFeedAt).format(
                        'MMM D, YYYY h:mm A'
                      )}
                    </InfoValue>
                  </InfoItem>
                )}
                {intervention.declinedAt && (
                  <>
                    <InfoItem>
                      <InfoLabel>Declined At</InfoLabel>
                      <InfoValue>
                        {dayjs(intervention.declinedAt).format(
                          'MMM D, YYYY h:mm A'
                        )}
                      </InfoValue>
                    </InfoItem>
                    {intervention.declineReason && (
                      <InfoItem style={{ gridColumn: '1 / -1' }}>
                        <InfoLabel>Decline Reason</InfoLabel>
                        <InfoValue>{intervention.declineReason}</InfoValue>
                      </InfoItem>
                    )}
                  </>
                )}
              </InfoGrid>
            </CardContent>
          </InfoCard>
        </Box>
      )}

      <Box mb={3}>
        <InfoCard>
          <CardContent>
            <SectionTitle>
              <CodeIcon />
              Trigger Data
            </SectionTitle>
            <TriggerDataBox>
              <pre>{JSON.stringify(triggerData, null, 2)}</pre>
            </TriggerDataBox>
          </CardContent>
        </InfoCard>
      </Box>

      {intervention.message && (
        <Box mb={3}>
          <InfoCard>
            <CardContent>
              <SectionTitle>
                <MessageIcon />
                Generated Message
              </SectionTitle>
              <MessageBox>{intervention.message}</MessageBox>
              {intervention.editedMessage &&
                intervention.editedMessage !== intervention.message && (
                  <>
                    <Typography
                      variant='subtitle2'
                      sx={{ mt: 2, mb: 1, color: 'text.secondary' }}
                    >
                      Edited Message (sent to user):
                    </Typography>
                    <MessageBox sx={{ backgroundColor: '#e3f2fd' }}>
                      {intervention.editedMessage}
                    </MessageBox>
                  </>
                )}
            </CardContent>
          </InfoCard>
        </Box>
      )}

      <ActionBar>
        <Button
          variant='contained'
          startIcon={<PlayIcon />}
          onClick={handleRetrigger}
          disabled={!intervention.user}
        >
          Trigger New {intervention.identifier}
        </Button>
        <Typography variant='caption' color='textSecondary'>
          This will create a new intervention for the same user
        </Typography>
      </ActionBar>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  )
}
